import * as React from 'react'

import { cn } from '@/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './DropdownMenu'

interface MoreTriggerState {
  /** True when the active item lives in the overflow menu. */
  active: boolean
}

interface PriorityNavProps<TItem> {
  items: TItem[]
  getKey: (item: TItem) => string
  isItemActive: (item: TItem) => boolean
  /** Renders one item in the visible bar. */
  renderItem: (item: TItem) => React.ReactNode
  /** Renders one item as a row inside the overflow menu. */
  renderMenuItem: (item: TItem) => React.ReactNode
  /** Renders the "More" trigger. Must return a single focusable element. */
  renderMoreTrigger: (state: MoreTriggerState) => React.ReactElement
  /** Applied to the outer box (padding, height, background, border). */
  className?: string
  /** Applied to the overflow menu content. */
  contentClassName?: string
  /** Pixel gap between items. Drives both the layout and the fit math. */
  gap?: number
}

/**
 * A horizontal nav that never overflows. It measures each item against the
 * available width and pushes whatever does not fit into a "More" dropdown, so
 * the active item is always reachable.
 */
export function PriorityNav<TItem>({
  items,
  getKey,
  isItemActive,
  renderItem,
  renderMenuItem,
  renderMoreTrigger,
  className,
  contentClassName,
  gap = 4,
}: PriorityNavProps<TItem>) {
  const visibleRowRef = React.useRef<HTMLDivElement>(null)
  const measureRowRef = React.useRef<HTMLDivElement>(null)
  const moreTriggerRef = React.useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(items.length)

  const measure = React.useCallback(() => {
    const visibleRow = visibleRowRef.current
    const measureRow = measureRowRef.current
    if (!visibleRow || !measureRow) return

    const available = visibleRow.clientWidth
    const widths = Array.from(measureRow.children).map(
      (node) => (node as HTMLElement).getBoundingClientRect().width,
    )

    const fullWidth =
      widths.reduce((sum, width) => sum + width, 0) +
      gap * Math.max(0, widths.length - 1)

    if (fullWidth <= available) {
      setVisibleCount(widths.length)
      return
    }

    const moreWidth = moreTriggerRef.current?.getBoundingClientRect().width ?? 0
    let used = moreWidth
    let fit = 0
    for (const width of widths) {
      const nextUsed = used + gap + width
      if (nextUsed > available) break
      used = nextUsed
      fit += 1
    }
    setVisibleCount(fit)
  }, [gap])

  // Measure before paint so the bar never flashes its full, overflowing form.
  React.useLayoutEffect(() => {
    measure()
  }, [measure, items])

  React.useEffect(() => {
    const visibleRow = visibleRowRef.current
    const measureRow = measureRowRef.current
    if (!visibleRow) return

    const observer = new ResizeObserver(() => measure())
    observer.observe(visibleRow)
    if (measureRow) observer.observe(measureRow)
    return () => observer.disconnect()
  }, [measure])

  const safeCount = Math.min(visibleCount, items.length)
  const visibleItems = items.slice(0, safeCount)
  const overflowItems = items.slice(safeCount)
  const hasActiveOverflow = overflowItems.some(isItemActive)

  return (
    <div className={cn('relative w-full', className)}>
      {/*
        Hidden full-width copy used only to read each item's natural width. It
        must clip its own overflow: the items are shrink-0, so an unclipped row
        would grow the page's scroll width and show a stray horizontal scrollbar.
      */}
      <div
        ref={measureRowRef}
        aria-hidden
        inert
        className="pointer-events-none invisible absolute inset-0 flex items-center overflow-hidden"
        style={{ gap }}
      >
        {items.map((item) => (
          <React.Fragment key={getKey(item)}>{renderItem(item)}</React.Fragment>
        ))}
      </div>
      <div
        ref={moreTriggerRef}
        aria-hidden
        inert
        className="pointer-events-none invisible absolute left-0 top-0"
      >
        {renderMoreTrigger({ active: false })}
      </div>

      <div
        ref={visibleRowRef}
        className="flex h-full w-full items-center overflow-hidden"
        style={{ gap }}
      >
        {visibleItems.map((item) => (
          <React.Fragment key={getKey(item)}>{renderItem(item)}</React.Fragment>
        ))}
        {overflowItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {renderMoreTrigger({ active: hasActiveOverflow })}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={contentClassName}>
              {overflowItems.map((item) => (
                <React.Fragment key={getKey(item)}>
                  {renderMenuItem(item)}
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
